import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import {
  buildAuthorizeUrl,
  buildClaudeDeepLink,
  buildMcpConfigJson,
  clearOAuthArtifacts,
  completeGrant,
  fetchConnectionState,
  isStateValid,
  readOAuthCallback,
  revokeConnection,
  type ConnectionState,
  type ConnectionStatus,
  type McpTarget,
} from "../services/claudeConnectionService";

// ===========================================================================
// Configuración → Conexión con Claude: guided flow to connect a Claude client
// (Desktop / Code) to THIS tenant's MCP server.
//
//   1. Destino       — qué tenant/entorno apunta el MCP (se confirma antes)
//   2. Consentimiento — lanza authorize (PKCE) + pantalla de Coding Cloud
//   3. Configuración  — config/URL copiable y deep-link para añadir en Claude
//   4. Estado         — confirmación + estado de conexión en vivo
//
// On returning from the consent screen (?code&state), the grant completes
// automatically and the status is refreshed. See claudeConnectionService.
// ===========================================================================

type TC = ReturnType<typeof buildThemeClasses>;
type WizardStep = "destino" | "consentimiento" | "configuracion" | "estado";

const STEPS: { key: WizardStep; label: string }[] = [
  { key: "destino", label: "Destino" },
  { key: "consentimiento", label: "Consentimiento" },
  { key: "configuracion", label: "Configuración" },
  { key: "estado", label: "Estado" },
];

const STATUS_META: Record<ConnectionStatus, { label: string; tone: string; icon: string }> = {
  connected: { label: "Conectado", tone: "bg-emerald-500/15 text-emerald-500", icon: "ri-checkbox-circle-line" },
  pending: { label: "Conectando…", tone: "bg-amber-500/15 text-amber-500", icon: "ri-loader-4-line" },
  disconnected: { label: "Sin conectar", tone: "bg-gray-400/15 text-gray-500", icon: "ri-plug-line" },
};

export default function ClaudeConnectionSettings() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses();

  const [state, setState] = useState<ConnectionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState<WizardStep>("destino");

  const refresh = useCallback(async () => {
    setState(await fetchConnectionState());
  }, []);

  // Initial load + handle the consent-screen callback (?code&state) on return.
  useEffect(() => {
    let active = true;
    (async () => {
      const callback = readOAuthCallback();
      if (callback) {
        if (!isStateValid(callback.state)) {
          clearOAuthArtifacts();
          Swal.fire({
            icon: "error",
            title: "Consentimiento no válido",
            text: "El estado de la autorización no coincide. Vuelve a iniciar la conexión.",
            confirmButtonColor: brand.primary,
          });
          await refresh();
          if (active) setLoading(false);
          return;
        }
        // Returning from consent: complete the grant and confirm.
        setWizardOpen(true);
        setStep("estado");
        setState((prev) => (prev ? { ...prev, status: "pending" } : prev));
        try {
          const next = await completeGrant(callback.code, callback.state);
          if (active) setState(next);
        } catch {
          Swal.fire({
            icon: "error",
            title: "No se pudo completar la conexión",
            text: "El servidor rechazó la autorización. Inténtalo de nuevo.",
            confirmButtonColor: brand.primary,
          });
          await refresh();
        } finally {
          clearOAuthArtifacts();
        }
      } else {
        await refresh();
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [refresh]);

  const target = state?.target ?? null;

  const openWizard = () => {
    setStep("destino");
    setWizardOpen(true);
  };

  const status = state?.status ?? "disconnected";
  const meta = STATUS_META[status];

  const handleDisconnect = async () => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Desconectar Claude",
      text: "Se revocará el acceso de Claude a este MCP. Podrás volver a conectarlo cuando quieras.",
      showCancelButton: true,
      confirmButtonText: "Desconectar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#DC2626",
    });
    if (!res.isConfirmed) return;
    try {
      await revokeConnection();
    } catch {
      /* backend optional — still reflect locally */
    }
    await refresh();
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500/15 text-orange-500">
              <i className="ri-robot-2-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Conexión con Claude</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Conecta tu Claude (Desktop / Code) al MCP de este espacio para consultar tus datos
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              refresh().finally(() => setLoading(false));
            }}
            title="Refrescar estado"
            className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
          >
            <i className="ri-refresh-line" />
          </button>
        </div>

        {loading ? (
          <div className={`rounded-2xl border border-dashed p-10 text-center ${tc.cardBorder} ${tc.cardBg}`}>
            <i className="ri-loader-4-line mb-2 block animate-spin text-2xl text-orange-500" />
            <p className={`text-sm ${tc.textSecondary}`}>Cargando estado de conexión…</p>
          </div>
        ) : (
          <div className={`rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
            {/* Live status */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${meta.tone}`}>
                  <i className={`${meta.icon} ${status === "pending" ? "animate-spin" : ""}`} />
                  {meta.label}
                </span>
                {state?.account && (
                  <span className={`text-sm ${tc.textSecondary}`}>{state.account}</span>
                )}
                {state?.lastConnectedAt && (
                  <span className={`text-xs ${tc.textSecondary}`}>
                    Última conexión: {new Date(state.lastConnectedAt).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {status === "connected" && (
                  <button
                    onClick={handleDisconnect}
                    className="rounded-lg border border-red-500/40 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10"
                  >
                    <i className="ri-link-unlink mr-1" />
                    Desconectar
                  </button>
                )}
                <button
                  onClick={openWizard}
                  className="flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400"
                >
                  <i className="ri-links-line" />
                  {status === "connected" ? "Reconfigurar" : "Conectar con Claude"}
                </button>
              </div>
            </div>

            {/* Target summary (siempre visible: a qué tenant/entorno apunta) */}
            {target && <TargetSummary target={target} tc={tc} />}
          </div>
        )}
      </div>

      {wizardOpen && target && (
        <Modal
          title="Conectar con Claude"
          onClose={() => setWizardOpen(false)}
          themeClasses={tc}
          content={
            <ConnectWizard
              tc={tc}
              isDarkMode={isDarkMode}
              target={target}
              step={step}
              status={status}
              setStep={setStep}
              onRefresh={refresh}
              onClose={() => setWizardOpen(false)}
            />
          }
        />
      )}
    </div>
  );
}

// --- Pieces -----------------------------------------------------------------

function TargetSummary({ target, tc }: { target: McpTarget; tc: TC }) {
  const rows: { label: string; value: string }[] = [
    { label: "Tenant", value: target.tenantName },
    { label: "Entorno", value: target.environment },
    ...(target.company ? [{ label: "Empresa", value: target.company }] : []),
    { label: "Servidor MCP", value: target.serverName },
  ];
  return (
    <div className={`rounded-xl border border-dashed p-4 ${tc.cardBorder}`}>
      <p className={`mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${tc.textSecondary}`}>
        <i className="ri-focus-3-line" />
        A dónde apunta esta conexión
      </p>
      <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-2 text-sm">
            <span className={tc.textSecondary}>{r.label}</span>
            <span className={`max-w-[60%] truncate font-medium ${tc.text}`} title={r.value}>
              {r.value}
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-2 text-sm sm:col-span-2">
          <span className={tc.textSecondary}>URL</span>
          <code className={`max-w-[70%] truncate ${tc.text}`} title={target.serverUrl}>
            {target.serverUrl}
          </code>
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ current, tc }: { current: WizardStep; tc: TC }) {
  const activeIdx = STEPS.findIndex((s) => s.key === current);
  return (
    <div className="mb-5 flex items-center">
      {STEPS.map((s, idx) => {
        const done = idx < activeIdx;
        const active = idx === activeIdx;
        return (
          <div key={s.key} className="flex flex-1 items-center last:flex-none">
            <div className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                  active
                    ? "bg-primary-500 text-white"
                    : done
                      ? "bg-emerald-500 text-white"
                      : `border ${tc.border} ${tc.textSecondary}`
                }`}
              >
                {done ? <i className="ri-check-line" /> : idx + 1}
              </span>
              <span className={`hidden text-xs sm:inline ${active ? tc.text : tc.textSecondary}`}>
                {s.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`mx-2 h-px flex-1 ${done ? "bg-emerald-500" : tc.border.replace("border", "bg")}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ConnectWizard({
  tc,
  isDarkMode,
  target,
  step,
  status,
  setStep,
  onRefresh,
  onClose,
}: {
  tc: TC;
  isDarkMode: boolean;
  target: McpTarget;
  step: WizardStep;
  status: ConnectionStatus;
  setStep: (s: WizardStep) => void;
  onRefresh: () => Promise<void>;
  onClose: () => void;
}) {
  const [launching, setLaunching] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const configJson = buildMcpConfigJson(target);
  const deepLink = buildClaudeDeepLink(target);

  const copy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo copiar.", confirmButtonColor: brand.primary });
    }
  };

  const launchConsent = async () => {
    setLaunching(true);
    try {
      const url = await buildAuthorizeUrl(target);
      // Full-page redirect: the consent screen returns to this same section,
      // where the grant completes automatically (see readOAuthCallback).
      window.location.href = url;
    } catch {
      setLaunching(false);
      Swal.fire({
        icon: "error",
        title: "No se pudo iniciar el consentimiento",
        text: "Revisa la configuración del MCP e inténtalo de nuevo.",
        confirmButtonColor: brand.primary,
      });
    }
  };

  const codeBox = `rounded-lg border p-3 text-xs font-mono overflow-x-auto ${
    isDarkMode ? "border-dark-border bg-[#1b1b1b] text-gray-200" : "border-gray-200 bg-gray-50 text-gray-800"
  }`;
  const primaryBtn =
    "flex items-center justify-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-400 disabled:opacity-60";
  const ghostBtn = `rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`;

  return (
    <div>
      <StepIndicator current={step} tc={tc} />

      {/* Step 1 — Destino */}
      {step === "destino" && (
        <div className="space-y-4">
          <p className={`text-sm ${tc.textSecondary}`}>
            Vas a autorizar a Claude para acceder al MCP de este espacio. Confirma a dónde apunta
            antes de continuar:
          </p>
          <TargetSummary target={target} tc={tc} />
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className={ghostBtn}>
              Cancelar
            </button>
            <button onClick={() => setStep("consentimiento")} className={primaryBtn}>
              Continuar
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — Consentimiento OAuth */}
      {step === "consentimiento" && (
        <div className="space-y-4">
          <p className={`text-sm ${tc.textSecondary}`}>
            Te llevaremos a la pantalla de consentimiento de <strong>{target.tenantName}</strong>{" "}
            para autorizar la conexión de forma segura (OAuth con PKCE). Al terminar, volverás aquí
            automáticamente.
          </p>
          <div className={`flex items-start gap-2 rounded-xl border border-dashed p-3 text-xs ${tc.cardBorder} ${tc.textSecondary}`}>
            <i className="ri-shield-check-line mt-0.5 text-emerald-500" />
            <span>
              No compartimos tu contraseña con Claude. El consentimiento usa un código de un solo uso
              (PKCE) y puedes revocarlo cuando quieras desde esta misma pantalla.
            </span>
          </div>
          <div className="flex justify-between gap-2 pt-1">
            <button onClick={() => setStep("destino")} className={ghostBtn}>
              <i className="ri-arrow-left-line mr-1" />
              Atrás
            </button>
            <button onClick={launchConsent} disabled={launching} className={primaryBtn}>
              {launching ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Abriendo…
                </>
              ) : (
                <>
                  <i className="ri-external-link-line" />
                  Iniciar consentimiento
                </>
              )}
            </button>
          </div>
          <button
            onClick={() => setStep("configuracion")}
            className={`block w-full text-center text-xs ${tc.textSecondary} hover:text-primary-500`}
          >
            Ya completé el consentimiento — ver configuración
          </button>
        </div>
      )}

      {/* Step 3 — Configuración / deep-link */}
      {step === "configuracion" && (
        <div className="space-y-4">
          <p className={`text-sm ${tc.textSecondary}`}>
            Añade este servidor MCP en Claude. Copia la configuración en tu{" "}
            <code>claude_desktop_config.json</code> (o úsala en Claude Code / Windsurf):
          </p>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className={`text-xs font-medium ${tc.textSecondary}`}>Configuración MCP</label>
              <button
                onClick={() => copy("config", configJson)}
                className={`text-xs ${copied === "config" ? "text-emerald-500" : tc.textSecondary} hover:text-primary-500`}
              >
                <i className={`mr-0.5 ${copied === "config" ? "ri-check-line" : "ri-file-copy-line"}`} />
                {copied === "config" ? "Copiado" : "Copiar"}
              </button>
            </div>
            <pre className={codeBox}>{configJson}</pre>
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className={`text-xs font-medium ${tc.textSecondary}`}>URL del servidor MCP</label>
              <button
                onClick={() => copy("url", target.serverUrl)}
                className={`text-xs ${copied === "url" ? "text-emerald-500" : tc.textSecondary} hover:text-primary-500`}
              >
                <i className={`mr-0.5 ${copied === "url" ? "ri-check-line" : "ri-file-copy-line"}`} />
                {copied === "url" ? "Copiado" : "Copiar"}
              </button>
            </div>
            <code className={`block truncate ${codeBox}`} title={target.serverUrl}>
              {target.serverUrl}
            </code>
          </div>

          {deepLink && (
            <a
              href={deepLink}
              className={`${primaryBtn} w-full`}
              target="_blank"
              rel="noreferrer"
            >
              <i className="ri-add-circle-line" />
              Añadir a Claude (deep-link)
            </a>
          )}

          <div className="flex justify-between gap-2 pt-1">
            <button onClick={() => setStep("consentimiento")} className={ghostBtn}>
              <i className="ri-arrow-left-line mr-1" />
              Atrás
            </button>
            <button
              onClick={async () => {
                await onRefresh();
                setStep("estado");
              }}
              className={primaryBtn}
            >
              Verificar conexión
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      )}

      {/* Step 4 — Estado / confirmación */}
      {step === "estado" && (
        <div className="space-y-4 text-center">
          {status === "connected" ? (
            <>
              <i className="ri-checkbox-circle-line mx-auto block text-5xl text-emerald-500" />
              <h3 className={`text-lg font-semibold ${tc.text}`}>¡Claude está conectado!</h3>
              <p className={`text-sm ${tc.textSecondary}`}>
                Claude ya puede consultar los datos de <strong>{target.tenantName}</strong> (
                {target.environment}) a través del MCP.
              </p>
            </>
          ) : status === "pending" ? (
            <>
              <i className="ri-loader-4-line mx-auto block animate-spin text-5xl text-amber-500" />
              <h3 className={`text-lg font-semibold ${tc.text}`}>Completando la conexión…</h3>
              <p className={`text-sm ${tc.textSecondary}`}>
                Estamos confirmando el consentimiento. Esto tarda unos segundos.
              </p>
            </>
          ) : (
            <>
              <i className="ri-error-warning-line mx-auto block text-5xl text-amber-500" />
              <h3 className={`text-lg font-semibold ${tc.text}`}>Aún no detectamos la conexión</h3>
              <p className={`text-sm ${tc.textSecondary}`}>
                Si ya completaste el consentimiento y añadiste el MCP en Claude, refresca el estado.
              </p>
            </>
          )}

          <div className="flex justify-center gap-2 pt-1">
            <button onClick={() => setStep("configuracion")} className={ghostBtn}>
              <i className="ri-arrow-left-line mr-1" />
              Ver configuración
            </button>
            {status === "connected" ? (
              <button onClick={onClose} className={primaryBtn}>
                Finalizar
              </button>
            ) : (
              <button onClick={() => onRefresh()} className={primaryBtn}>
                <i className="ri-refresh-line" />
                Refrescar estado
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
